import { useEffect, useCallback } from 'react'
import { ChevronLeft, ChevronRight, X } from 'lucide-react'
import type { WalkthroughStep } from '../../services/types'

interface WalkthroughOverlayProps {
  steps: WalkthroughStep[]
  currentIndex: number
  onPrev: () => void
  onNext: () => void
  onClose: () => void
}

export default function WalkthroughOverlay({
  steps,
  currentIndex,
  onPrev,
  onNext,
  onClose,
}: WalkthroughOverlayProps) {
  const step = steps[currentIndex]

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    const tag = (document.activeElement?.tagName ?? '').toLowerCase()
    if (tag === 'textarea' || tag === 'input') return

    if (e.key === 'ArrowLeft') {
      e.preventDefault()
      onPrev()
    } else if (e.key === 'ArrowRight') {
      e.preventDefault()
      onNext()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      onClose()
    }
  }, [onPrev, onNext, onClose])

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [handleKeyDown])

  if (!step) return null

  return (
    <div className="absolute bottom-3 left-3 right-3 z-10 glass-dark rounded-xl px-4 py-3 font-mono">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] text-slate-500 tracking-widest uppercase">
          Step {currentIndex + 1} of {steps.length}
        </span>
        <button
          type="button"
          onClick={onClose}
          className="text-slate-500 hover:text-slate-300 transition-colors"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Title + Description */}
      <div className="text-sm text-cyan-300 font-semibold mb-1">{step.title}</div>
      <div className="text-xs text-slate-400 leading-relaxed mb-3">{step.description}</div>

      {/* Footer: line range + nav */}
      <div className="flex items-center justify-between">
        <span className="text-[10px] text-slate-600">
          Lines {step.startLine}&ndash;{step.endLine}
        </span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={onPrev}
            disabled={currentIndex === 0}
            className="p-1 rounded hover:bg-white/5 text-slate-400 disabled:text-slate-700 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={onNext}
            disabled={currentIndex === steps.length - 1}
            className="p-1 rounded hover:bg-white/5 text-slate-400 disabled:text-slate-700 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  )
}
